import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { buildGroupTree, getAllGroupIds } from "@dashboard/shared/components/GroupFilterHeader/GroupFilterHeader.utils";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { IconButton, List, ListItem, ListItemButton, ListItemText, Tooltip, Typography } from "@mui/material";

import { useStyles } from "./GroupTree.style";
import { GroupTreeItem } from "./GroupTreeItem";

type Props = {
    selectedGroupId: number | null;
    onSelect: (groupId: number | null) => void;
    isOpen: boolean;
    onToggle: () => void;
};

export const GroupTree = (props: Props) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["sidepanels", "common"]);
    const { data: workspace } = useCurrentWorkspace();
    const groups = useSelector(selectGroups);
    const groupTree = React.useMemo(() => buildGroupTree(groups), [groups]);

    const [expandedGroups, setExpandedGroups] = React.useState<number[]>([]);

    const onToggleExpand = (groupId: number) => {
        setExpandedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    };

    const allGroupIds = React.useMemo(() => getAllGroupIds(groupTree), [groupTree]);
    const hasExpandable = groupTree.some((group) => group.children.length > 0);
    const allExpanded = React.useMemo(() => {
        if (allGroupIds.length === 0) {
            return false;
        }
        const expandedSet = new Set(expandedGroups);
        return allGroupIds.every((id) => expandedSet.has(id));
    }, [allGroupIds, expandedGroups]);

    const onToggleAll = () => {
        setExpandedGroups(allExpanded ? [] : allGroupIds);
    };

    if (!props.isOpen) {
        return (
            <div className={classes.rootCollapsed}>
                <Tooltip title={t("common:button.expandSidebar")} placement="right">
                    <IconButton size="small" onClick={props.onToggle}>
                        <i className="fa-solid fa-angle-right" style={{ fontSize: 12 }} />
                    </IconButton>
                </Tooltip>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <div className={hasExpandable ? classes.toolbar : classes.toolbarNoExpand}>
                {hasExpandable ? (
                    <Tooltip title={allExpanded ? t("common:button.collapseAll") : t("common:button.expandAll")}>
                        <IconButton size="small" onClick={onToggleAll}>
                            <i
                                className={cx(classes.toggleAllIcon, "fas fa-solid", allExpanded ? "fa-minimize" : "fa-maximize")}
                            />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <Tooltip title={t("common:button.collapseSidebar")} placement="right">
                    <IconButton size="small" onClick={props.onToggle} sx={{ marginLeft: "auto" }}>
                        <i className="fa-solid fa-angle-left" style={{ fontSize: 12 }} />
                    </IconButton>
                </Tooltip>
            </div>
            <List disablePadding className={classes.treeList}>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={props.selectedGroupId === null}
                        onClick={() => props.onSelect(null)}
                        className={`${classes.workspaceItem} ${classes.treeRowBg}`}
                        dense
                    >
                        <i className={cx("fa-solid fa-briefcase", classes.workspaceIcon)} />
                        <ListItemText
                            className={classes.listItemText}
                            primary={
                                <Typography className={classes.navText} noWrap>
                                    {workspace?.name ?? t("common:label.workspace")}
                                </Typography>
                            }
                        />
                    </ListItemButton>
                </ListItem>
                {groupTree.map((group) => (
                    <GroupTreeItem
                        key={group.id}
                        group={group}
                        depth={1}
                        selectedGroupId={props.selectedGroupId}
                        expandedGroups={expandedGroups}
                        onSelect={props.onSelect}
                        onToggleExpand={onToggleExpand}
                    />
                ))}
            </List>
        </div>
    );
};
